import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/game-config';
import { addScore } from '@/storage/score-repository';
import { playMenuNavigate, playMenuSelect } from '@/audio/sound-generator';
import { NEON_FONTS } from '@/config/colors';

const FONT_TITLE = NEON_FONTS.TITLE;
const FONT_BODY = NEON_FONTS.BODY;
const MAX_INITIALS = 3;
const CURSOR_BLINK_RATE_MS = 400;
const VALID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

interface InitialsEntryData {
  readonly score: number;
  readonly level: number;
}

export class InitialsEntryScene extends Phaser.Scene {
  private score = 0;
  private level = 1;
  private initials: string[] = [];
  private cursorIndex = 0;
  private initialsDisplay: Phaser.GameObjects.Text | null = null;
  private cursorVisible = true;
  private cursorTimer: Phaser.Time.TimerEvent | null = null;
  private isSubmitting = false;

  constructor() {
    super({ key: 'InitialsEntryScene' });
  }

  init(data: InitialsEntryData): void {
    this.score = data.score ?? 0;
    this.level = data.level ?? 1;
    this.initials = [];
    this.cursorIndex = 0;
    this.isSubmitting = false;
  }

  create(): void {
    this.cleanupObjects();

    const centerX = GAME_WIDTH / 2;

    // Title
    this.add.text(centerX, GAME_HEIGHT * 0.12, 'NEW HIGH SCORE!', {
      fontFamily: FONT_TITLE,
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#FFE500',
      shadow: { offsetX: 0, offsetY: 0, color: '#FFE500', blur: 12, fill: true },
    }).setOrigin(0.5);

    // Score display
    this.add.text(centerX, GAME_HEIGHT * 0.22, `SCORE: ${this.score}`, {
      fontFamily: FONT_BODY,
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#FFFFFF',
    }).setOrigin(0.5);

    // Level display
    this.add.text(centerX, GAME_HEIGHT * 0.29, `LEVEL: ${this.level}`, {
      fontFamily: FONT_BODY,
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#00FF88',
      shadow: { offsetX: 0, offsetY: 0, color: '#00FF88', blur: 6, fill: true },
    }).setOrigin(0.5);

    // "Enter your initials" prompt
    this.add.text(centerX, GAME_HEIGHT * 0.42, 'ENTER YOUR INITIALS', {
      fontFamily: FONT_BODY,
      fontSize: '13px',
      color: '#667788',
    }).setOrigin(0.5);

    // Initials display (3 underscores initially)
    this.initialsDisplay = this.add.text(centerX, GAME_HEIGHT * 0.55, '_ _ _', {
      fontFamily: FONT_TITLE,
      fontSize: '32px',
      fontStyle: 'bold',
      color: '#FFFFFF',
      shadow: { offsetX: 0, offsetY: 0, color: '#00E5FF', blur: 10, fill: true },
    }).setOrigin(0.5);

    // Instructions
    this.add.text(centerX, GAME_HEIGHT * 0.72, 'A-Z TO TYPE', {
      fontFamily: FONT_BODY,
      fontSize: '11px',
      color: '#556677',
    }).setOrigin(0.5);

    this.add.text(centerX, GAME_HEIGHT * 0.78, 'BACKSPACE TO DELETE', {
      fontFamily: FONT_BODY,
      fontSize: '11px',
      color: '#556677',
    }).setOrigin(0.5);

    this.add.text(centerX, GAME_HEIGHT * 0.84, 'ENTER TO CONFIRM', {
      fontFamily: FONT_BODY,
      fontSize: '11px',
      color: '#556677',
    }).setOrigin(0.5);

    // Cursor blink timer
    this.cursorTimer = this.time.addEvent({
      delay: CURSOR_BLINK_RATE_MS,
      callback: this.toggleCursor,
      callbackScope: this,
      loop: true,
    });

    this.setupKeyboard();
    this.updateDisplay();
  }

  private setupKeyboard(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) {
      return;
    }

    keyboard.on('keydown', (event: KeyboardEvent) => {
      if (this.isSubmitting) {
        return;
      }

      const key = event.key.toUpperCase();

      if (key === 'ENTER') {
        this.submitInitials();
        return;
      }

      if (key === 'BACKSPACE') {
        this.deleteCharacter();
        return;
      }

      if (key.length === 1 && VALID_CHARS.includes(key)) {
        this.addCharacter(key);
      }
    });
  }

  private addCharacter(char: string): void {
    if (this.cursorIndex >= MAX_INITIALS) {
      return;
    }

    this.initials[this.cursorIndex] = char;
    this.cursorIndex = Math.min(this.cursorIndex + 1, MAX_INITIALS);
    this.cursorVisible = true;
    playMenuNavigate(this);
    this.updateDisplay();
  }

  private deleteCharacter(): void {
    if (this.cursorIndex <= 0 && this.initials.length === 0) {
      return;
    }

    if (this.cursorIndex > 0) {
      this.cursorIndex -= 1;
      this.initials.splice(this.cursorIndex, 1);
    }

    this.cursorVisible = true;
    playMenuNavigate(this);
    this.updateDisplay();
  }

  private submitInitials(): void {
    if (this.initials.length === 0) {
      return;
    }

    this.isSubmitting = true;
    playMenuSelect(this);

    // Pad initials to show what was typed (1-3 chars allowed)
    const initialsStr = this.initials.join('').padEnd(1, ' ').substring(0, MAX_INITIALS);

    const entry = {
      id: crypto.randomUUID(),
      initials: initialsStr.trim(),
      score: this.score,
      levelReached: this.level,
      date: new Date().toISOString(),
    };

    addScore(entry);

    // Flash the initials neon yellow to confirm
    if (this.initialsDisplay) {
      this.initialsDisplay.setColor('#FFE500');
    }

    // Brief delay then show leaderboard
    this.time.delayedCall(800, () => {
      this.scene.start('LeaderboardScene', {
        highlightId: entry.id,
      });
    });
  }

  private toggleCursor(): void {
    this.cursorVisible = !this.cursorVisible;
    this.updateDisplay();
  }

  private updateDisplay(): void {
    if (!this.initialsDisplay) {
      return;
    }

    const chars: string[] = [];
    for (let i = 0; i < MAX_INITIALS; i++) {
      if (i < this.initials.length) {
        chars.push(this.initials[i]);
      } else if (i === this.cursorIndex && this.cursorVisible) {
        chars.push('_');
      } else {
        chars.push(' ');
      }
    }

    this.initialsDisplay.setText(chars.join(' '));
  }

  private cleanupObjects(): void {
    if (this.cursorTimer) {
      this.cursorTimer.destroy();
      this.cursorTimer = null;
    }
    this.initialsDisplay = null;
  }
}
